import React, { createContext, useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link, useRouteMatch } from "react-router-dom";

import { Layout } from "~/components/ui/layout";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { useLoadingDelay } from "~/hooks/use-loading-delay";
import type { Loading } from "~/types/common";

export function DocumentsLayout({ children, bareLayout = false }: { children: React.ReactElement; bareLayout?: boolean }) {
  const [breadcrumbHeading, setBreadcrumbHeading] = useState<Loading<string>>("");

  const isOfferRoute = Boolean(useRouteMatch("/pages/documents/offer"));
  const isAgreementRoute = Boolean(useRouteMatch("/pages/documents/:doctor_agreement_id"));
  const displayBreadcrumbs = isOfferRoute || isAgreementRoute;

  const displayLoading = useLoadingDelay(breadcrumbHeading == undefined, { delay: 200 });

  return (
    <Layout chrome={!bareLayout} contentClassName="max-sm:-tw-mt-6">
      <Portlet as="main">
        <PortletTitle iconClassName="icon-doc">
          {displayBreadcrumbs ? (
            <Link to="/pages/documents" className="max-sm:tw-leading-9">
              <FormattedMessage id="top.menu.documents" />
            </Link>
          ) : (
            <span className="max-sm:tw-leading-9">
              <FormattedMessage id="top.menu.documents" />
            </span>
          )}

          {displayBreadcrumbs ? (
            <span className="tw-mx-2 tw-inline-block" aria-hidden="true">
              /
            </span>
          ) : null}

          {displayBreadcrumbs ? (
            displayLoading ? (
              <Skeleton width={200} height={17} className="tw-rounded-md" />
            ) : (
              breadcrumbHeading
            )
          ) : null}
        </PortletTitle>

        <div className="portlet-body">
          {displayBreadcrumbs ? (
            <DocumentsBreadcrumbsContent.Provider value={setBreadcrumbHeading}>
              {children}
            </DocumentsBreadcrumbsContent.Provider>
          ) : (
            children
          )}
        </div>
      </Portlet>
    </Layout>
  );
}

type SetBreadcrumbHeading = React.Dispatch<React.SetStateAction<Loading<string>>>;

const DocumentsBreadcrumbsContent = createContext<SetBreadcrumbHeading | undefined>(undefined);

export function useDocumentsBreadcrumb(heading: Loading<string>): void {
  const setBreadcrumbHeading = useContext(DocumentsBreadcrumbsContent);

  useEffect(() => {
    // The redesign shell has its own breadcrumb and does not provide this context.
    if (!setBreadcrumbHeading) return;
    setBreadcrumbHeading(heading);

    return () => {
      setBreadcrumbHeading("");
    };
  }, [heading, setBreadcrumbHeading]);
}
